import clsx from 'clsx';
import * as React from 'react';

import { TextAlignment, type VerticalAlignment } from '@gitbook/api-client';
import {
    BLOCK_HEADING_1,
    BLOCK_HEADING_2,
    BLOCK_HEADING_3,
    type TableColumnDefinition,
    type TextColumnDefinition,
} from '@gitbook/doc-core';
import {
    createPalette,
    PaletteCommandType,
    type PaletteDef,
    type PaletteGroupDef,
} from '@gitbook/react-uikit';
import { FragmentRenderer } from '@gitbook/slate-react';
import { Icon } from '@gitbook/spine-icons';
import { assertNever } from '@gitbook/util-ts';

import { TextAlignmentContext, textAlignmentClassName } from '../../text-alignment';
import {
    horizontalAlignmentIcons,
    horizontalAlignments,
    verticalAlignmentIcons,
    verticalAlignments,
} from '../utils/alignments';
import { VerticalAlignmentContext } from '../utils/contexts';
import type {
    RecordValueRenderProps,
    TableDefinitionCommandsParams,
    TableDefinitionRenderSpec,
} from './types';

export const textDefinitionSpec: TableDefinitionRenderSpec<string, TextColumnDefinition> = {
    title: 'Text',
    description: '',
    icon: Icon.ColumnTypeText,
    ValueComponent: RecordTextValue,
    commands: computeTextColumnCommands,
};

function computeTextColumnCommands(
    params: TableDefinitionCommandsParams<TextColumnDefinition>
): PaletteGroupDef[] {
    const { definition, view } = params;
    const { textAlignment = 'left', verticalAlignment = 'middle' } = definition;

    const commands = [
        {
            key: 'text.alignment',
            title: 'Horizontal alignment',
            descendant: () => createTextAlignmentOptionsPalette(params),
            icon: horizontalAlignmentIcons[textAlignment],
        },
    ];

    /*
     * In cards, v. alignment currently makes no sense as the wrapper's height is determined by the text itself, and only in tables does the height of another column (aka card) impact the height of the other column/card.
     * tldr: vertical alignment is useful in tables, not in cards.
     */
    if (view.type !== 'cards') {
        commands.push({
            key: 'text.vertical',
            title: 'Vertical alignment',
            descendant: () => createVerticalAlignmentOptionsPalette(params),
            icon: verticalAlignmentIcons[verticalAlignment],
        });
    }

    return [
        {
            key: 'text.alignment',
            commands,
        },
    ];
}

function createTextAlignmentOptionsPalette(
    params: TableDefinitionCommandsParams<TextColumnDefinition>
): PaletteDef {
    const { definition, setDefinition } = params;

    return createPalette({
        groups: [
            {
                key: 'text.alignment.select',
                commands: horizontalAlignments.map((option) => ({
                    key: `text.alignment.${option.value}`,
                    title: option.label,
                    type: PaletteCommandType.RADIO,
                    selected: definition.textAlignment === option.value,
                    icon: option.icon,
                    trigger: () => {
                        const [, newCommandParams] = setDefinition({
                            ...definition,
                            textAlignment: option.value as TextColumnDefinition['textAlignment'],
                        });
                        return createTextAlignmentOptionsPalette(newCommandParams);
                    },
                })),
            },
        ],
    });
}

function createVerticalAlignmentOptionsPalette(
    params: TableDefinitionCommandsParams<TextColumnDefinition>
): PaletteDef {
    const { definition, setDefinition } = params;

    return createPalette({
        groups: [
            {
                key: 'text.vertical.column.select',
                commands: verticalAlignments.map((option) => ({
                    key: `text.vertical.${option.value}`,
                    title: option.label,
                    type: PaletteCommandType.RADIO,
                    selected:
                        definition.verticalAlignment === option.value ||
                        (definition.verticalAlignment === undefined && option.value === 'middle'),
                    icon: option.icon,
                    trigger: () => {
                        setDefinition({
                            ...definition,
                            verticalAlignment: option.value as VerticalAlignment,
                        });
                    },
                })),
            },
        ],
    });
}

/**
 * Cell content for a Text column.
 */
function RecordTextValue(
    props: RecordValueRenderProps<string, TextColumnDefinition>
): React.ReactElement | null {
    const { nodeKey, editor, value, definition, readOnly, view } = props;
    const verticalAlignment = React.useContext(VerticalAlignmentContext);

    const tableBlock = editor.value.document.assertBlock(nodeKey);
    const fragment = tableBlock.getFragmentByKey(value);

    if (value === undefined || value === null || !fragment) {
        return null;
    }

    /* We only show a placeholder in a table's cards view and not when user inserts a heading block */
    const isHeading = fragment
        .getBlocks()
        .some((block) => [BLOCK_HEADING_1, BLOCK_HEADING_2, BLOCK_HEADING_3].includes(block.type));
    const placeholder = view?.type === 'cards' && !readOnly && !isHeading ? 'Type something…' : '';

    const textAlignment = getColumnTextAlignment(definition);

    return (
        <div
            className={clsx('flex flex-1', textAlignmentClassName[textAlignment], {
                'cursor-text': !readOnly,
                'items-center': verticalAlignment === 'middle',
                'items-start': verticalAlignment === 'top',
                'items-end': verticalAlignment === 'bottom',
            })}
        >
            <TextAlignmentContext.Provider value={textAlignment}>
                <FragmentRenderer
                    fragment={value}
                    placeholder={placeholder}
                    horizontalAlignment={definition.textAlignment}
                />
            </TextAlignmentContext.Provider>
        </div>
    );
}

/**
 * Get the text alignment for a column definition.
 */
export function getColumnTextAlignment(definition: TableColumnDefinition): TextAlignment {
    if (definition.type !== 'text') {
        return TextAlignment.Start;
    }

    const textAlignment = definition.textAlignment;
    switch (textAlignment) {
        case 'left':
            return TextAlignment.Start;
        case 'center':
            return TextAlignment.Center;
        case 'right':
            return TextAlignment.End;
        default:
            assertNever(textAlignment);
    }
}
